import { ColDef, ColDefField, ISetFilterParams, SetFilterValuesFuncParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-enterprise';
import { t } from 'i18next';
import { capitalizeFirstLetter } from '../capitalize';

export const createDateColumn = <T = any>(field: ColDefField<T, any>, headerKey: string): ColDef<T> => ({
    headerName: capitalizeFirstLetter(t(headerKey)),
    field: field,
    cellDataType: 'date',
    filter: 'agDateColumnFilter',
    valueGetter: (params: ValueGetterParams<T>): Date | null => {
        if (!params.data) return null;
        const fieldValue = (params.data as any)[field as string] as string | null | undefined;
        if (!fieldValue) return null;
        const isoString = fieldValue.replace(' ', 'T');
        const date = new Date(isoString);
        return isNaN(date.getTime()) ? null : date;
    },
    valueFormatter: (params: ValueFormatterParams<T>): string => {
        if (!params.value) return '';
        try {
            return new Date(params.value).toLocaleDateString(t('locale'));
        } catch {
            return '';
        }
    },
    filterParams: {
        comparator: (filterDate: Date, cellValue: Date): number => {
            if (!cellValue) return -1;

            const filterDateOnly = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
            const cellDateOnly = new Date(cellValue.getFullYear(), cellValue.getMonth(), cellValue.getDate());

            if (cellDateOnly < filterDateOnly) return -1;
            if (cellDateOnly > filterDateOnly) return 1;
            return 0;
        },
        browserDatePicker: true,
        minValidYear: 2000,
    },
    colId: headerKey,
});

export const createTimeColumn = <T = any>(field: ColDefField<T, any>, headerKey: string): ColDef<T> => ({
    headerName: capitalizeFirstLetter(t(headerKey)),
    field: field,
    valueGetter: (params: ValueGetterParams<T>): string | null => {
        if (!params.data) return null;
        const fieldValue = (params.data as any)[field as string] as string | null | undefined;
        if (!fieldValue) return null;

        const date = new Date(fieldValue);
        return date.toLocaleTimeString(t('locale'), {
            hour: '2-digit',
            minute: '2-digit',
        });
    },
    filter: 'agSetColumnFilter',
    filterParams: {
        values: (params: SetFilterValuesFuncParams) => {
            const values = new Set<string>();
            params.api.forEachNodeAfterFilter((node) => {
                if (node.data) {
                    const fieldValue = (node.data as any)[field as string];
                    if (fieldValue) {
                        const date = new Date(fieldValue);
                        values.add(
                            date.toLocaleTimeString(t('locale'), {
                                hour: '2-digit',
                                minute: '2-digit',
                            })
                        );
                    }
                }
            });
            const sortedValues = Array.from(values).sort();
            params.success(sortedValues);
        },
    } as ISetFilterParams,
    valueFormatter: (params: ValueFormatterParams<T>): string => params.value || '',
    colId: headerKey,
});

export const createDateTimeColumn = <T = any>(field: ColDefField<T, any>, headerKey: string): ColDef<T> => ({
    headerName: capitalizeFirstLetter(t(headerKey)),
    field: field,
    cellDataType: 'dateTime',
    filter: 'agDateColumnFilter',
    valueGetter: (params: ValueGetterParams<T>): Date | null => {
        if (!params.data) return null;
        const fieldValue = (params.data as any)[field as string] as string | null | undefined;
        if (!fieldValue) return null;

        const isoString = fieldValue.replace(' ', 'T');
        const date = new Date(isoString);
        return isNaN(date.getTime()) ? null : date;
    },
    valueFormatter: (params: ValueFormatterParams<T>): string => {
        if (!params.value) return '';
        try {
            return new Date(params.value).toLocaleString(t('locale'));
        } catch {
            return '';
        }
    },
    colId: headerKey,
});
